import React, { useState, useRef, useEffect } from 'react';

function MessageBubble({ message, isOwn, showSenderName, onEdit, onDelete }) {
  const sender = message.sender || {};
  const name = [sender.first_name, sender.last_name].filter(Boolean).join(' ') || sender.email || '';
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content || '');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!editing) setEditValue(message.content || '');
  }, [message.content, editing]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleSaveEdit = () => {
    const trimmed = editValue?.trim();
    if (trimmed !== undefined && trimmed !== (message.content || '')) {
      onEdit?.(message.id, trimmed);
    }
    setEditing(false);
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this message?')) {
      onDelete?.(message.id);
    }
    setMenuOpen(false);
  };

  const canEdit = isOwn && typeof onEdit === 'function';
  const canDelete = isOwn && typeof onDelete === 'function';
  const showActions = (canEdit || canDelete) && !editing;

  return (
    <div className={'flex ' + (isOwn ? 'justify-end' : 'justify-start') + ' mb-3 group'}>
      <div className={'flex flex-col max-w-[75%] ' + (isOwn ? 'items-end' : 'items-start')}>
        <div
          className={
            'rounded-lg px-3 py-2 ' +
            (isOwn ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-900')
          }
        >
          {!isOwn && showSenderName && name ? (
            <p className="text-xs font-medium opacity-80 mb-0.5">{name}</p>
          ) : null}
          {editing ? (
            <div className="min-w-[200px]">
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="w-full text-sm text-gray-900 rounded border border-gray-300 p-2 resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setMenuOpen(false); }}
                  className="text-xs font-medium text-gray-600 hover:underline"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm whitespace-pre-wrap break-words">{message.content || ''}</p>
              {message.is_edited && (
                <p className={'text-xs mt-0.5 ' + (isOwn ? 'text-blue-200' : 'text-gray-400')}>
                  (edited)
                </p>
              )}
              <div className="flex items-center justify-end gap-1 mt-1">
                <p className={'text-xs ' + (isOwn ? 'text-blue-100' : 'text-gray-500')}>
                  {message.created_at
                    ? new Date(message.created_at).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''}
                </p>
                {showActions && (
                  <div className="relative ml-1" ref={menuRef}>
                    <button
                      type="button"
                      onClick={() => setMenuOpen((o) => !o)}
                      className="p-0.5 rounded hover:bg-black/10"
                      aria-label="Message options"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    {menuOpen && (
                      <div className="absolute right-0 top-full mt-1 py-1 bg-white rounded shadow-lg border border-gray-200 text-gray-900 text-sm z-20 min-w-[100px]">
                        {canEdit && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-1.5 hover:bg-gray-100"
                            onClick={() => { setEditing(true); setMenuOpen(false); }}
                          >
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-red-600"
                            onClick={handleDelete}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageBubble;
